/**
 * Order Aggregators Setup integration component
 * Handles UberEats and DoorDash integration with separate setup tabs
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Truck, Save, CheckCircle, ExternalLink, Copy, Users, Info } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus, SetupMethod } from '@/types/onboarding';

// Validation schema - using actual database columns
const aggregatorsSetupSchema = z.object({
  uber_aggregation_status: z.nativeEnum(OnboardingStatus).optional(),
  uber_setup_method: z.nativeEnum(SetupMethod).optional(),
  ubereats_store_id: z.string().optional(),
  doordash_aggregation_status: z.nativeEnum(OnboardingStatus).optional(),
  doordash_setup_method: z.nativeEnum(SetupMethod).optional(),
  doordash_store_id: z.string().optional(),
  doordash_login: z.string().optional(),
  doordash_pin: z.string().optional(),
});

type AggregatorsSetupFormValues = z.infer<typeof aggregatorsSetupSchema>;

const AggregatorSetup: React.FC = () => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('ubereats');

  const sectionStatus = getSectionStatus(OnboardingSection.AGGREGATORS);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.AGGREGATORS), [onboardingData]);

  const form = useForm<AggregatorsSetupFormValues>({
    resolver: zodResolver(aggregatorsSetupSchema),
    defaultValues: {
      uber_aggregation_status: OnboardingStatus.NOT_STARTED,
      uber_setup_method: SetupMethod.DIY,
      ubereats_store_id: '',
      doordash_aggregation_status: OnboardingStatus.NOT_STARTED,
      doordash_setup_method: SetupMethod.DIY,
      doordash_store_id: '',
      doordash_login: '',
      doordash_pin: '',
    },
  });

  // Load existing data
  useEffect(() => {
    if (sectionData) {
      form.reset({
        uber_aggregation_status: sectionData.uber_aggregation_status || OnboardingStatus.NOT_STARTED,
        uber_setup_method: sectionData.uber_setup_method || SetupMethod.DIY,
        ubereats_store_id: sectionData.ubereats_store_id || '',
        doordash_aggregation_status: sectionData.doordash_aggregation_status || OnboardingStatus.NOT_STARTED,
        doordash_setup_method: sectionData.doordash_setup_method || SetupMethod.DIY,
        doordash_store_id: sectionData.doordash_store_id || '',
        doordash_login: sectionData.doordash_login || '',
        doordash_pin: sectionData.doordash_pin || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Save section data
  const handleSave = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.AGGREGATORS, formData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving aggregators setup:', error);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.AGGREGATORS, formData);
      await markSectionComplete(OnboardingSection.AGGREGATORS);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error marking aggregators setup complete:', error);
    }
  };

  // Check if can mark complete
  const canMarkComplete = () => {
    return true; // This section can always be marked complete as it's optional
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Truck className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold">Order Aggregators</h2>
          <p className="text-sm text-muted-foreground">
            Optional integration with UberEats and DoorDash (Additional fees apply)
          </p>
        </div>
        <div className="flex-shrink-0">
          <Badge 
            variant={sectionStatus === OnboardingStatus.COMPLETE ? "default" : "secondary"}
          >
            {sectionStatus}
          </Badge>
        </div>
      </div>

      {/* Success confirmation dialog */}
      {sectionStatus === OnboardingStatus.COMPLETE && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            This integration has been successfully set up and verified.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="ubereats">UberEats</TabsTrigger>
              <TabsTrigger value="doordash">DoorDash</TabsTrigger>
            </TabsList>

            <TabsContent value="ubereats" className="space-y-6 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-500 text-white rounded flex items-center justify-center font-bold">U</div>
                <div>
                  <h3 className="font-medium text-lg">UberEats Setup</h3>
                  <p className="text-sm text-muted-foreground">Set up your UberEats integration</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Step 1: Open UberEats merchant portal */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">1</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Open your UberEats merchant portal</div>
                    </div>
                  </div>

                  <div className="ml-11">
                    <p className="text-sm text-muted-foreground mb-3">
                      Access your UberEats store management dashboard
                    </p>
                    <Button asChild>
                      <a
                        href="https://merchants.ubereats.com/manager/stores"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open UberEats Manager
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Step 2: Select store */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">2</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Select the store you want to connect to Pump'd</div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Copy store ID */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">3</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Copy and paste the store id from the end of the URL</div>
                    </div>
                  </div>

                  <div className="ml-11 space-y-4">
                    {/* UberEats store ID mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
                      <p className="text-xs font-medium text-gray-700">UberEats URL Example:</p>
                      <div className="bg-white p-3 rounded border font-mono text-sm">
                        https://merchants.ubereats.com/manager/stores/<span className="bg-yellow-200 px-1">a3b14c15-926d-53e5-f899-79323f8f462</span>
                      </div>
                      <p className="text-xs text-gray-600">Copy the highlighted store ID from your URL</p>
                    </div>

                    {/* Store ID input */}
                    <div className="space-y-2">
                      <Label htmlFor="ubereats_store_id">UberEats Store ID</Label>
                      <Input
                        id="ubereats_store_id"
                        placeholder="Paste your store ID here"
                        value={form.watch('ubereats_store_id') || ''}
                        onChange={(e) => {
                          form.setValue('ubereats_store_id', e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 4: Add Pump'd support */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">4</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Add Pump'd support as a user to let us complete the integration</div>
                    </div>
                  </div>

                  <div className="ml-11 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Navigate to the users section to add our support team
                    </p>

                    {/* Direct link button */}
                    {form.watch('ubereats_store_id') && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Quick link to add user:</p>
                        <Button asChild variant="outline">
                          <a
                            href={`https://merchants.ubereats.com/manager/users/add?restaurantUUID=${form.watch('ubereats_store_id')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Add User to UberEats
                          </a>
                        </Button>
                      </div>
                    )}

                    {/* UberEats sidebar mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Select 'Users'</p>
                      <div className="bg-white rounded border shadow-sm w-48">
                        <div className="space-y-1 p-2">
                          <div className="flex items-center gap-3 px-2 py-2 text-gray-700 hover:bg-gray-100 rounded">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            <span className="text-sm">Menu</span>
                          </div>
                          <div className="flex items-center gap-3 px-2 py-2 text-gray-700 hover:bg-gray-100 rounded">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-sm">Payments</span>
                          </div>
                          <div className="flex items-center gap-3 px-2 py-2 bg-gray-200 text-gray-900 rounded">
                            <Users className="w-4 h-4 text-gray-700" />
                            <span className="text-sm font-medium">Users</span>
                          </div>
                          <div className="flex items-center gap-3 px-2 py-2 text-gray-700 hover:bg-gray-100 rounded">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <span className="text-sm">Delivery settings</span>
                          </div>
                          <div className="flex items-center gap-3 px-2 py-2 text-gray-700 hover:bg-gray-100 rounded">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-sm">Settings</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Add user button mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Click 'Add user'</p>
                      <div className="bg-white p-3 rounded border shadow-sm">
                        <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded text-sm font-medium">
                          <svg className="w-4 h-4 stroke-white" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                          Add user
                        </div>
                      </div>
                    </div>

                    {/* User details to copy */}
                    <div className="bg-blue-50 p-4 rounded-lg border">
                      <p className="text-xs font-medium text-blue-700 mb-3">→ Add 'gianni@pumpd.co.nz' as a user to the account and click 'Add'</p>
                      
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs">First Name</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              Gianni
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('Gianni')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Last Name</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              Munro
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('Munro')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Email</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              gianni@pumpd.co.nz
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('gianni@pumpd.co.nz')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* What happens next section */}
                <div className="space-y-4 mt-8 pt-6 pb-6 border-t">
                  <h4 className="font-medium">What happens next:</h4>
                  
                  <div className="space-y-4">
                    {/* Point 1 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            Our team will handle all the technical setup for you
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Point 2 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            Your orders will be automatically synced into your Pump'd dashboard
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Point 3 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            All dockets will be printed from the Pump'd printer
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Info Note */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          Contact your account manager to discuss adding QR codes to receipts
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </TabsContent>

            <TabsContent value="doordash" className="space-y-6 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-500 text-white rounded flex items-center justify-center font-bold">DD</div>
                <div>
                  <h3 className="font-medium text-lg">DoorDash Setup</h3>
                  <p className="text-sm text-muted-foreground">Set up your DoorDash integration</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Step 1: Identify DoorDash version */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">1</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Identify which version of DoorDash your restaurant is using</div>
                    </div>
                  </div>

                  <div className="ml-11 space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">→ Look at the back of the DoorDash tablet</p>
                      <p className="text-sm text-muted-foreground">→ Find the sticker with login and PIN (can be hidden behind the tablet stand / case)</p>
                      <p className="text-sm text-muted-foreground">→ Add the credentials below</p>
                    </div>

                    {/* Login/PIN Form */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                      <div className="space-y-2">
                        <Label htmlFor="doordash_login">Login *</Label>
                        <Input
                          id="doordash_login"
                          placeholder="123456789102yz@ipad.doordash.com"
                          value={form.watch('doordash_login') || ''}
                          onChange={(e) => {
                            form.setValue('doordash_login', e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="doordash_pin">PIN *</Label>
                        <Input
                          id="doordash_pin"
                          placeholder="1234"
                          value={form.watch('doordash_pin') || ''}
                          onChange={(e) => {
                            form.setValue('doordash_pin', e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </div>
                    </div>

                    {/* Info Card */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          If you're unable to locate the sticker, that's all good! You can skip steps 1 and 2. Just use the button below to open the DoorDash merchant portal and continue from step 3.
                          <br /><br />
                          If the credentials are found, simply complete step 2 to add the store ID. Once this is done, you can save progress and mark this section as complete!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2: Get DoorDash Store ID */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">2</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Open your DoorDash merchant portal</div>
                    </div>
                  </div>

                  <div className="ml-11 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Access your DoorDash store management dashboard
                    </p>
                    <Button asChild>
                      <a
                        href="https://www.doordash.com/merchant/summary"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open DoorDash Merchant
                      </a>
                    </Button>

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">→ Select the store you want to connect to Pump'd</p>
                      <p className="text-sm text-muted-foreground">→ Copy and paste the store id from the end of the URL</p>
                    </div>

                    {/* DoorDash store ID mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
                      <p className="text-xs font-medium text-gray-700">DoorDash URL Example:</p>
                      <div className="bg-white p-3 rounded border font-mono text-sm">
                        https://www.doordash.com/merchant/summary?store_id=<span className="bg-yellow-200 px-1">12345678</span>
                      </div>
                      <p className="text-xs text-gray-600">Copy the highlighted store ID from your URL</p>
                    </div>

                    {/* Store ID input */}
                    <div className="space-y-2">
                      <Label htmlFor="doordash_store_id">DoorDash Store ID</Label>
                      <Input
                        id="doordash_store_id"
                        placeholder="Paste your store ID here"
                        value={form.watch('doordash_store_id') || ''}
                        onChange={(e) => {
                          form.setValue('doordash_store_id', e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 3: Add Pump'd support as user */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">3</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Add Pump'd support as a user to let us complete the integration</div>
                    </div>
                  </div>

                  <div className="ml-11 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Navigate to the users section to add our support team
                    </p>

                    {/* DoorDash Navigation mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Select 'Manage users'</p>
                      <div className="bg-white rounded border shadow-sm w-48">
                        <div className="space-y-1 p-2">
                          <div className="flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded">
                            <Users className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-medium">Customers</span>
                            <div className="ml-auto">
                              <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded">
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" strokeWidth={2} />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                            <span className="text-sm font-medium">Financials</span>
                          </div>
                          <div className="flex items-center gap-3 px-3 py-2 bg-red-50 text-red-700 rounded font-medium">
                            <Users className="w-5 h-5 text-red-600" />
                            <span className="text-sm font-medium">Manage users</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* DoorDash Add user button mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Click 'Add user'</p>
                      <div className="bg-white p-3 rounded border shadow-sm">
                        <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                          <svg className="w-4 h-4 stroke-white" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                          Add user
                        </div>
                      </div>
                    </div>

                    {/* User details to copy */}
                    <div className="bg-blue-50 p-4 rounded-lg border">
                      <p className="text-xs font-medium text-blue-700 mb-3">→ Add 'gianni@pumpd.co.nz' as a user to the account</p>
                      
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs">First Name</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              Gianni
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('Gianni')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Last Name</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              Munro
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('Munro')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Email</Label>
                          <div className="flex items-center gap-2">
                            <div className="bg-white p-2 rounded border flex-1 text-sm font-mono">
                              gianni@pumpd.co.nz
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText('gianni@pumpd.co.nz')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional steps */}
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">→ Select 'Store manager'</p>
                    </div>

                    {/* Add stores link mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Select the store(s) you want to connect to Pump'd</p>
                      <div className="bg-white p-3 rounded border shadow-sm">
                        <div className="flex items-center gap-2 text-gray-700 text-sm font-medium underline">
                          <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                          Add stores
                        </div>
                      </div>
                    </div>

                    {/* Send invite button mockup */}
                    <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-gray-700">→ Click 'Send invite'</p>
                      <div className="bg-white p-3 rounded border shadow-sm">
                        <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                          Send invite
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What happens next section */}
                <div className="space-y-4 mt-8 pt-6 pb-6 border-t">
                  <h4 className="font-medium">What happens next:</h4>
                  
                  <div className="space-y-4">
                    {/* Point 1 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            Our team will handle all the technical setup for you
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Point 2 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            Your orders will be automatically synced into your Pump'd dashboard
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Point 3 */}
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        <CheckCircle className="h-4 w-4 stroke-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1">
                            All dockets will be printed from the Pump'd printer
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Info Note */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          Contact your account manager to discuss adding QR codes to receipts
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-6 border-t">
            <div className="text-sm text-muted-foreground">
              {hasUnsavedChanges && "You have unsaved changes"}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save Progress'}
              </Button>

              <Button
                onClick={handleMarkComplete}
                disabled={!canMarkComplete() || isMarkingComplete || hasUnsavedChanges}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {isMarkingComplete ? 'Submitting...' : 'Mark as Complete'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AggregatorSetup;